import React, { useRef, useEffect, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { GRID_SPECS, NAVIGATION_CONFIG, NAVIGATION_EVENT_TYPES, getEventColor, getEventBorderColor } from '../../../config/navigationConfig';

// Stable empty array reference to avoid re-renders from default prop
const EMPTY_EVENTS = [];

const NavigationRowCanvas = memo(({
  rowIndex,
  data,
  viewWindow,
  cardiacEvents = EMPTY_EVENTS,
  onClick,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const eventRectsRef = useRef([]);

  // Get translated description for a cardiac event
  const getEventDescription = useCallback((event) => {
    const params = event.params || {};

    // Format duration: 1 decimal for short durations (pauses), whole seconds for longer episodes
    const formatDuration = (seconds) => {
      if (seconds == null) return '?';
      return seconds < 10 ? seconds.toFixed(1) : Math.round(seconds).toString();
    };

    switch (event.eventType) {
      case NAVIGATION_EVENT_TYPES.CardiacPause:
        return t('recordView.multiChart.ecg.navigation.events.cardiacPause', {
          duration: formatDuration(params.durationSeconds)
        });

      case NAVIGATION_EVENT_TYPES.Bradycardia:
        return t('recordView.multiChart.ecg.navigation.events.bradycardia', {
          heartRate: params.heartRate || '?',
          duration: formatDuration(params.durationSeconds)
        });

      case NAVIGATION_EVENT_TYPES.Tachycardia:
        return t('recordView.multiChart.ecg.navigation.events.tachycardia', {
          heartRate: params.heartRate || '?',
          duration: formatDuration(params.durationSeconds)
        });

      case NAVIGATION_EVENT_TYPES.IrregularBeat:
        if (params.beatCount === 1) {
          return t('recordView.multiChart.ecg.navigation.events.irregularBeatSingle');
        }
        return t('recordView.multiChart.ecg.navigation.events.irregularBeatMultiple', {
          beatCount: params.beatCount || '?'
        });

      default:
        // Fallback to server-provided description
        return event.description;
    }
  }, [t]);

  // Draw the grid on canvas
  const drawGrid = useCallback((ctx, width, height, minMv, maxMv) => {
    // Clear canvas
    ctx.fillStyle = GRID_SPECS.colors.background;
    ctx.fillRect(0, 0, width, height);

    // Calculate grid dimensions
    const msPerPixel = (60 * 1000) / width; // 60 seconds per row
    const mvRange = maxMv - minMv;
    const mvPerPixel = mvRange / height;

    // Draw minor grid lines (vertical - time)
    ctx.strokeStyle = GRID_SPECS.colors.minorGrid;
    ctx.lineWidth = GRID_SPECS.lineWidth.minorGrid;
    ctx.beginPath();
    
    const minorGridXPixels = GRID_SPECS.minorGridX / msPerPixel;
    for (let x = 0; x <= width; x += minorGridXPixels) {
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
    }
    
    // Draw minor grid lines (horizontal - voltage)
    const minorGridYPixels = GRID_SPECS.minorGridY / mvPerPixel;
    for (let y = 0; y <= height; y += minorGridYPixels) {
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
    }
    ctx.stroke();

    // Draw major grid lines (vertical - time)
    ctx.strokeStyle = GRID_SPECS.colors.majorGrid;
    ctx.lineWidth = GRID_SPECS.lineWidth.majorGrid;
    ctx.beginPath();
    
    const majorGridXPixels = GRID_SPECS.majorGridX / msPerPixel;
    for (let x = 0; x <= width; x += majorGridXPixels) {
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
    }
    
    // Draw major grid lines (horizontal - voltage)
    const majorGridYPixels = GRID_SPECS.majorGridY / mvPerPixel;
    for (let y = 0; y <= height; y += majorGridYPixels) {
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
    }
    ctx.stroke();

    // Draw zero line (0mV) - only if 0 is within the range
    if (minMv <= 0 && maxMv >= 0) {
      // Calculate where 0mV should be positioned
      const zeroY = height - ((0 - minMv) * (height / (maxMv - minMv)));
      
      ctx.strokeStyle = '#888';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, zeroY);
      ctx.lineTo(width, zeroY);
      ctx.stroke();
    }
  }, []);

  // Draw ECG waveform from chunks with their time positions
  const drawECG = useCallback((ctx, width, height, chunks, minMv, maxMv, sampleRate) => {
    if (!chunks || chunks.length === 0) return;

    const mvToPixels = height / (maxMv - minMv);
    const msPerRow = NAVIGATION_CONFIG.MINUTES_PER_ROW * 60 * 1000;
    const msPerPixel = msPerRow / width;

    ctx.strokeStyle = GRID_SPECS.colors.ecgLine;
    ctx.lineWidth = GRID_SPECS.lineWidth.ecg;

    // Draw each chunk at its correct time position
    chunks.forEach(chunk => {
      const { startMs, data: ecgData } = chunk;

      if (!ecgData || ecgData.length === 0) return;

      // Calculate X position where this chunk should start (based on time)
      const startPixelX = startMs / msPerPixel;

      // Calculate how many pixels this chunk should occupy (based on sample count and time)
      const chunkDurationMs = (ecgData.length / sampleRate) * 1000;
      const chunkWidthPixels = chunkDurationMs / msPerPixel;

      const samplesPerPixel = ecgData.length / chunkWidthPixels;

      ctx.beginPath();

      // Draw this chunk's data
      for (let pixelOffset = 0; pixelOffset < chunkWidthPixels; pixelOffset++) {
        const pixelX = startPixelX + pixelOffset;

        // Skip if outside canvas bounds
        if (pixelX < 0 || pixelX > width) continue;

        const startSample = Math.floor(pixelOffset * samplesPerPixel);
        const endSample = Math.min(
          Math.floor((pixelOffset + 1) * samplesPerPixel),
          ecgData.length
        );

        if (startSample >= endSample) continue;

        // Find min/max in this pixel's sample range
        let pixelMinVal = ecgData[startSample];
        let pixelMaxVal = ecgData[startSample];

        for (let i = startSample + 1; i < endSample; i++) {
          if (ecgData[i] < pixelMinVal) pixelMinVal = ecgData[i];
          if (ecgData[i] > pixelMaxVal) pixelMaxVal = ecgData[i];
        }

        // Map values to screen coordinates using dynamic range
        // Note: Y increases downward in canvas, so we need to flip
        const yMin = height - ((pixelMaxVal - minMv) * mvToPixels);
        const yMax = height - ((pixelMinVal - minMv) * mvToPixels);

        if (pixelOffset === 0) {
          // Start of this chunk
          const firstY = height - ((ecgData[startSample] - minMv) * mvToPixels);
          ctx.moveTo(pixelX, firstY);
        } else {
          // Connect to previous point within this chunk
          ctx.lineTo(pixelX, yMin);
          if (Math.abs(yMax - yMin) > 1) {
            // Draw vertical line if there's a significant difference
            ctx.lineTo(pixelX, yMax);
          }
        }
      }

      ctx.stroke();
    });
  }, []);

  // Draw view window overlay
  const drawViewWindow = useCallback((ctx, width, height, window) => {
    if (!window) return;

    const startX = width * window.startRatio;
    const endX = width * window.endRatio;

    // Draw semi-transparent overlay
    ctx.fillStyle = GRID_SPECS.colors.viewWindow;
    ctx.fillRect(startX, 0, endX - startX, height);

    // Draw borders
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(startX, 0);
    ctx.lineTo(startX, height);
    ctx.moveTo(endX, 0);
    ctx.lineTo(endX, height);
    ctx.stroke();
  }, []);

  // Draw cardiac event highlights
  const drawCardiacEvents = useCallback((ctx, width, height, events, currentRowIndex) => {
    if (!events || events.length === 0) {
      eventRectsRef.current = [];
      return;
    }

    const msPerRow = NAVIGATION_CONFIG.MINUTES_PER_ROW * 60 * 1000;
    const rowStartMs = currentRowIndex * msPerRow;
    const rowEndMs = rowStartMs + msPerRow;

    // Filter events that intersect with this row
    const rowEvents = events.filter(e => e.startMs < rowEndMs && e.endMs > rowStartMs);
    eventRectsRef.current = [];

    rowEvents.forEach(event => {
      // Calculate X positions relative to this row
      const eventStartInRow = Math.max(0, event.startMs - rowStartMs);
      const eventEndInRow = Math.min(msPerRow, event.endMs - rowStartMs);

      const startX = (eventStartInRow / msPerRow) * width;
      const endX = (eventEndInRow / msPerRow) * width;
      const rectWidth = endX - startX;

      // Use event-specific color for fill
      ctx.fillStyle = getEventColor(event.eventType);
      ctx.fillRect(startX, 0, rectWidth, height);

      // Draw border to distinguish adjacent events
      ctx.strokeStyle = getEventBorderColor(event.eventType);
      ctx.lineWidth = 1;
      ctx.strokeRect(startX, 0, rectWidth, height);

      // Store rect for hit testing (tooltip)
      eventRectsRef.current.push({ startX, endX, event });
    });
  }, []);

  // Handle mouse move for tooltip
  const handleMouseMove = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;

    // Find all overlapping events at this position
    const hitEvents = eventRectsRef.current.filter(r => x >= r.startX && x <= r.endX);

    if (hitEvents.length === 0) {
      canvas.title = '';
    } else if (hitEvents.length === 1) {
      canvas.title = getEventDescription(hitEvents[0].event);
    } else {
      // Combine descriptions for overlapping events
      canvas.title = hitEvents.map(r => getEventDescription(r.event)).join('\n');
    }
  }, [getEventDescription]);

  // Main render function
  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    // Use actual canvas dimensions, accounting for device pixel ratio
    const dpr = window.devicePixelRatio || 1;
    const width = canvas.width / dpr;
    const height = canvas.height / dpr;

    // Save context state and scale for high DPI
    ctx.save();
    ctx.scale(dpr, dpr);

    // Use fixed Y-axis limits from config - data outside this range will be clipped
    const minMv = GRID_SPECS.fixedYAxis.min;
    const maxMv = GRID_SPECS.fixedYAxis.max;

    // Draw in order: grid, cardiacEvents, ECG, view window
    drawGrid(ctx, width, height, minMv, maxMv);

    // Draw cardiac event highlights (after grid, before ECG)
    drawCardiacEvents(ctx, width, height, cardiacEvents, rowIndex);

    if (data && data.chunks && data.chunks.length > 0) {
      const sampleRate = data.sampleRate;
      drawECG(ctx, width, height, data.chunks, minMv, maxMv, sampleRate);
    }

    if (viewWindow) {
      drawViewWindow(ctx, width, height, viewWindow);
    }

    // Restore context state
    ctx.restore();
  }, [data, viewWindow, cardiacEvents, rowIndex, drawGrid, drawECG, drawViewWindow, drawCardiacEvents]);

  // Handle canvas resize
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const container = canvas.parentElement;
    const rect = container.getBoundingClientRect();
    
    // Set canvas size with device pixel ratio for sharp rendering
    const dpr = window.devicePixelRatio || 1;
    
    // Set internal canvas dimensions (resolution)
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    
    // Set CSS dimensions (display size) - this must match container
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    
    // Don't scale context - instead pass scaled dimensions to render
    // This will prevent the mismatch between logical and physical pixels
    
    // Re-render after resize
    render();
  }, [render]);

  // Handle click events
  const handleClick = useCallback((event) => {
    // Ignore clicks if disabled
    if (disabled) return;

    const canvas = canvasRef.current;
    if (!canvas || !onClick) return;

    const rect = canvas.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const canvasWidth = rect.width;

    onClick(clickX, canvasWidth);
  }, [onClick, disabled]);

  // Setup canvas on mount
  useEffect(() => {
    resizeCanvas();
    
    // Add resize observer
    const resizeObserver = new ResizeObserver(() => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      animationFrameRef.current = requestAnimationFrame(resizeCanvas);
    });

    const canvas = canvasRef.current;
    if (canvas && canvas.parentElement) {
      resizeObserver.observe(canvas.parentElement);
    }

    return () => {
      resizeObserver.disconnect();
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [resizeCanvas]);

  // Re-render when data, view window, or cardiac events change
  useEffect(() => {
    render();
  }, [data, viewWindow, cardiacEvents, render]);

  return (
    <canvas
      ref={canvasRef}
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      style={{
        display: 'block',
        width: '100%',
        height: '100%',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1
      }}
    />
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  // Only re-render if data, viewWindow, or cardiacEvents actually changed
  return (
    prevProps.rowIndex === nextProps.rowIndex &&
    prevProps.data === nextProps.data &&
    JSON.stringify(prevProps.viewWindow) === JSON.stringify(nextProps.viewWindow) &&
    prevProps.cardiacEvents === nextProps.cardiacEvents &&
    prevProps.onClick === nextProps.onClick &&
    prevProps.disabled === nextProps.disabled
  );
});

NavigationRowCanvas.displayName = 'NavigationRowCanvas';

export default NavigationRowCanvas;